

import React, { useState, useRef, useEffect, useMemo } from 'react';
import useAnalytics from '../hooks/useAnalytics';
import { useUser } from '../hooks/useUser';
import SummaryCard from '../components/analytics/SummaryCard';
import CallCountsChart from '../components/analytics/CallCountsChart';
import CallSuccessChart from '../components/analytics/CallSuccessChart';
import DateRangePicker from '../components/common/DateRangePicker';
import { formatDuration, formatDateShort } from '../utils/formatters';
import { useSettings } from '../contexts/SettingsContext';

const AnalyticsPage = () => {
  // Date range state
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  // Location filter
  const { selectedLocationId } = useSettings();
  const { groupId } = useUser();

  // Memoize filters to prevent unnecessary re-fetches
  const filters = useMemo(() => {
    const filterObj = {};

    if (dateRange.start) {
      const d = new Date(dateRange.start);
      d.setHours(0, 0, 0, 0);
      filterObj.start_date = d.getTime();
    }

    if (dateRange.end) {
      const d = new Date(dateRange.end);
      d.setHours(23, 59, 59, 999);
      filterObj.end_date = d.getTime();
    }

    if (selectedLocationId !== 'all') {
      filterObj.location_id = selectedLocationId;
    }

    if (selectedLocationId === 'all' && groupId) {
      filterObj.group_id = groupId;
    }

    return filterObj;
  }, [dateRange.start, dateRange.end, selectedLocationId, groupId]);

  // Fetch analytics data
  const { data, isLoading, error, refetch } = useAnalytics(filters);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle date range change
  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
  };

  // Get date range label
  const getDateRangeLabel = () => {
    if (!dateRange.start || !dateRange.end) {
      return 'All time';
    }
    const start = formatDateShort(dateRange.start);
    const end = formatDateShort(dateRange.end);
    return `${start} - ${end}`;
  };


  return (
    <div className="p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl text-white mb-2">Analytics</h1>
              <p className="text-gray-400">
                Monitor your call performance and trends
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
        {/* Filters Bar */}
        <div className="flex items-center space-x-3 mb-6">
          {/* Date Range Button */}
          <div className="relative" ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="inline-flex items-center px-4 py-2 border border-gray-700 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {getDateRangeLabel()}
            </button>

            {/* Date Picker Popup */}
            {showDatePicker && (
              <DateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={handleDateRangeChange}
                onClose={() => setShowDatePicker(false)}
              />
            )}
          </div>

          {/* Refresh Button */}
          <button
            onClick={refetch}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-700 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <svg className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Loading State - Full Page Overlay */}
        {isLoading && !data && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#3AD0E6]"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-12 w-12 rounded-full bg-gray-900"></div>
              </div>
            </div>
            <p className="mt-6 text-lg font-medium text-white">Loading Analytics...</p>
            <p className="mt-2 text-sm text-gray-400">
              This may take 3-5 seconds due to rate limiting
            </p>
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
              <div className="animate-pulse">●</div>
              <span>Fetching call data</span>
            </div>
          </div>
        )}

        {/* Error Message with Retry */}
        {error && !isLoading && (
          <div className="mb-6">
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-white mb-1">
                    Failed to Load Analytics
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">{error.message || error}</p>
                  <button
                    onClick={refetch}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <SummaryCard
              title="Call Counts"
              value={data?.summary?.total_calls?.toLocaleString() || '—'}
              subtitle="All agents"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              }
            />
            <SummaryCard
              title="Call Duration"
              value={data?.summary?.average_duration_ms ? formatDuration(data.summary.average_duration_ms) : '—'}
              subtitle="All agents"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
          </div>
        )}

        {/* Charts */}
        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Call Counts Chart - Takes 2 columns */}
            <div className="lg:col-span-2">
              <CallCountsChart 
                key={`calls-${dateRange.start}-${dateRange.end}-${selectedLocationId}`}
                data={data?.calls_over_time} 
                isLoading={isLoading && !!data} 
              />
            </div>

            {/* Call Transfer Rate Chart - Takes 1 column */}
            <div className="lg:col-span-1">
              <CallSuccessChart 
                key={`transfer-${dateRange.start}-${dateRange.end}-${selectedLocationId}`}
                data={data?.transfer_rate} 
                isLoading={isLoading && !!data} 
              />
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;

